import {Dialog, Transition} from '@headlessui/react'
import React, {Fragment, useEffect, useState} from "react";
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from "@/components/ui/textarea";
import {Checkbox} from "@/components/ui/checkbox";
import {CheckIcon, X} from "lucide-react";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {useRouter} from "next/navigation";
import {getLeadSourceValue} from "@/utils/leadSource";
import {CONTACT_WHATSAPP} from "@/lib/public-config";

export interface FormLeadProps {
    open: boolean;
    setOpen: any;
    title: string;
    marca: string;
    modello: string;
    articolo: string;
    textarea?: boolean;
    phone?: boolean;
    whatsappMessage?: string;
}

const phoneRegex = new RegExp(
    /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);

const formSchema = z.object({
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    email: z.string().min(1, {message: 'L\'email è obbligatoria.'}).email({message: 'Inserisci un indirizzo email valido.'}),
    telefono: z.string().min(1, {message: 'Il telefono è obbligatorio.'}).min(10, {message: 'Il numero di telefono deve avere almeno 10 cifre.'}).regex(phoneRegex, 'Telefono non valido'),
    provincia: z.string().min(1, {message: 'la provincia è obbligatoria.'}),
    terms: z.boolean().default(false).refine(val => val, {message: 'Devi accettare i termini e le condizioni.'}),
    description: z.string().optional()
});

export const provinces = [
    {code: "AG", name: "Agrigento"},
    {code: "AL", name: "Alessandria"},
    {code: "AN", name: "Ancona"},
    {code: "AO", name: "Aosta"},
    {code: "AR", name: "Arezzo"},
    {code: "AP", name: "Ascoli Piceno"},
    {code: "AT", name: "Asti"},
    {code: "AV", name: "Avellino"},
    {code: "BA", name: "Bari"},
    {code: "BT", name: "Barletta-Andria-Trani"},
    {code: "BL", name: "Belluno"},
    {code: "BN", name: "Benevento"},
    {code: "BG", name: "Bergamo"},
    {code: "BI", name: "Biella"},
    {code: "BO", name: "Bologna"},
    {code: "BZ", name: "Bolzano"},
    {code: "BS", name: "Brescia"},
    {code: "BR", name: "Brindisi"},
    {code: "CA", name: "Cagliari"},
    {code: "CL", name: "Caltanissetta"},
    {code: "CB", name: "Campobasso"},
    {code: "CI", name: "Carbonia-Iglesias"},
    {code: "CE", name: "Caserta"},
    {code: "CT", name: "Catania"},
    {code: "CZ", name: "Catanzaro"},
    {code: "CH", name: "Chieti"},
    {code: "CO", name: "Como"},
    {code: "CS", name: "Cosenza"},
    {code: "CR", name: "Cremona"},
    {code: "KR", name: "Crotone"},
    {code: "CN", name: "Cuneo"},
    {code: "EN", name: "Enna"},
    {code: "FM", name: "Fermo"},
    {code: "FE", name: "Ferrara"},
    {code: "FI", name: "Firenze"},
    {code: "FG", name: "Foggia"},
    {code: "FC", name: "Forlì-Cesena"},
    {code: "FR", name: "Frosinone"},
    {code: "GE", name: "Genova"},
    {code: "GO", name: "Gorizia"},
    {code: "GR", name: "Grosseto"},
    {code: "IM", name: "Imperia"},
    {code: "IS", name: "Isernia"},
    {code: "SP", name: "La Spezia"},
    {code: "LT", name: "Latina"},
    {code: "LE", name: "Lecce"},
    {code: "LC", name: "Lecco"},
    {code: "LI", name: "Livorno"},
    {code: "LO", name: "Lodi"},
    {code: "LU", name: "Lucca"},
    {code: "MC", name: "Macerata"},
    {code: "MN", name: "Mantova"},
    {code: "MS", name: "Massa-Carrara"},
    {code: "MT", name: "Matera"},
    {code: "VS", name: "Medio Campidano"},
    {code: "ME", name: "Messina"},
    {code: "MI", name: "Milano"},
    {code: "MO", name: "Modena"},
    {code: "MB", name: "Monza e della Brianza"},
    {code: "NA", name: "Napoli"},
    {code: "NO", name: "Novara"},
    {code: "NU", name: "Nuoro"},
    {code: "OG", name: "Ogliastra"},
    {code: "OT", name: "Olbia-Tempio"},
    {code: "OR", name: "Oristano"},
    {code: "PD", name: "Padova"},
    {code: "PA", name: "Palermo"},
    {code: "PR", name: "Parma"},
    {code: "PV", name: "Pavia"},
    {code: "PG", name: "Perugia"},
    {code: "PU", name: "Pesaro e Urbino"},
    {code: "PE", name: "Pescara"},
    {code: "PC", name: "Piacenza"},
    {code: "PI", name: "Pisa"},
    {code: "PT", name: "Pistoia"},
    {code: "PN", name: "Pordenone"},
    {code: "PZ", name: "Potenza"},
    {code: "PO", name: "Prato"},
    {code: "RG", name: "Ragusa"},
    {code: "RA", name: "Ravenna"},
    {code: "RC", name: "Reggio Calabria"},
    {code: "RE", name: "Reggio Emilia"},
    {code: "RI", name: "Rieti"},
    {code: "RN", name: "Rimini"},
    {code: "RM", name: "Roma"},
    {code: "RO", name: "Rovigo"},
    {code: "SA", name: "Salerno"},
    {code: "SS", name: "Sassari"},
    {code: "SV", name: "Savona"},
    {code: "SI", name: "Siena"},
    {code: "SR", name: "Siracusa"},
    {code: "SO", name: "Sondrio"},
    {code: "TA", name: "Taranto"},
    {code: "TE", name: "Teramo"},
    {code: "TR", name: "Terni"},
    {code: "TO", name: "Torino"},
    {code: "TP", name: "Trapani"},
    {code: "TN", name: "Trento"},
    {code: "TV", name: "Treviso"},
    {code: "TS", name: "Trieste"},
    {code: "UD", name: "Udine"},
    {code: "VA", name: "Varese"},
    {code: "VE", name: "Venezia"},
    {code: "VB", name: "Verbano-Cusio-Ossola"},
    {code: "VC", name: "Vercelli"},
    {code: "VR", name: "Verona"},
    {code: "VV", name: "Vibo Valentia"},
    {code: "VI", name: "Vicenza"},
    {code: "VT", name: "Viterbo"}
];

const FormLead = ({open, setOpen, title, marca, modello, articolo, phone, textarea = false, whatsappMessage}: FormLeadProps) => {
    const router = useRouter();
    const isWhatsappContact = Boolean(whatsappMessage) || title.toLowerCase().trim() == 'chatta con noi';
    const dialogTitle = isWhatsappContact ? 'Contattaci subito su Whatsapp' : title;
    const submitLabel = isWhatsappContact ? 'Continua' : 'Invia';

    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);


    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            cognome: '',
            email: '',
            telefono: '',
            provincia: '',
            description: '',
            terms: false
        }
    });

    const onSubmit = async (data: any) => {
        try {
            setIsLoading(true);


            // Deconstruisco i dati dal form
            const { nome, cognome, email, telefono, provincia, origine, argomento, motivazione, description } = data;

            // Preparo i dati da inviare nel body della richiesta
            const body = {
                origine: 'preventivo ' + articolo,
                argomento: 'LDGT',
                nome: nome ?? '',
                cognome: cognome ?? '',
                telefono: telefono ?? '',
                email: email ?? '',
                provincia: provincia ?? '',
                motivazione: title + ' ' + marca + ' ' + modello + ' ' + articolo + ' ' + data.description,
                fonte: getLeadSourceValue(),
            };

            // Invio la richiesta POST all'API
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            // Controllo la risposta
            const result = await response.json();

            console.log('Risultato della richiesta:', result);

            if (response.ok && result.status === 'success') {
                // Reindirizzo alla pagina thank-you con i parametri dinamici

                if (isWhatsappContact) {
                    const waUrl = whatsappMessage 
                        ? `https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(whatsappMessage)}`
                        : `https://wa.me/${CONTACT_WHATSAPP}`;
                    window.open(waUrl, '_blank');
                }

                const descrizione = title + ' ' + marca + ' ' + modello + ' ' + articolo;
                router.push(`/thank-you-page/vendita?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&cognome=${encodeURIComponent(cognome)}&provincia=${encodeURIComponent(provincia)}&telefono=${encodeURIComponent(telefono)}`);
                setIsLoading(false);

                if (phone) {
                    // avvia tel:link sul +393925419636
                    window.open(`tel:+393925419636`, '_blank');
                }

                // Chiudi il form
                setOpen(false);
            } else {
                // Gestisci l'errore, ad esempio mostrando un messaggio di errore all'utente
                setIsLoading(false);
                console.error('Errore durante l\'invio dei dati:', result);
            }
        } catch (error) {
            // Gestisci eventuali errori di rete o altri problemi
            console.error('Errore durante la richiesta:', error);
        }
    };

    useEffect(() => {
        if (!open) {
            setIsSuccess(false); // Reset on modal close
            setIsLoading(false);
        }
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        setIsSuccess(false); // Reset success state when closing the dialog
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog className="relative z-50" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-700 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-90"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-90"
                    >
                        <Dialog.Panel
                            className="max-h-[90vh] w-full max-w-4xl transform overflow-y-auto rounded-xl bg-white p-5 text-left shadow-2xl transition-all sm:p-8"
                        >
                            <div className="absolute right-4 top-4">
                                <button
                                    type="button"
                                    className="rounded-md bg-gray-100 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={() => setOpen(false)}
                                >
                                    <span className="sr-only">Close</span>
                                    <X className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            {isSuccess ? (
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <CheckIcon className="h-8 w-8 text-green-500" aria-hidden="true" />
                                    <h3 className="text-center text-xl font-semibold text-gray-900">
                                        Richiesta ricevuta con successo
                                    </h3>
                                </div>
                            ) : (
                                <div>
                                    <Dialog.Title as="h3" className="text-lg font-bold text-gray-900">
                                        {dialogTitle}
                                    </Dialog.Title>
                                    <div className="mt-4">
                                        <Form {...form}>
                                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                    {/* Campo Nome */}
                                                    <FormField
                                                        control={form.control}
                                                        name="nome"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Input
                                                                        {...field}
                                                                        className="border-gray-300"
                                                                        placeholder="Nome"
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                    {/* Campo Cognome */}
                                                    <FormField
                                                        control={form.control}
                                                        name="cognome"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Input
                                                                        {...field}
                                                                        className="border-gray-300"
                                                                        placeholder="Cognome"
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                    {/* Campo Email */}
                                                    <FormField
                                                        control={form.control}
                                                        name="email"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Input
                                                                        {...field}
                                                                        className="border-gray-300"
                                                                        placeholder="Indirizzo Email"
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                    {/* Campo Telefono */}
                                                    <FormField
                                                        control={form.control}
                                                        name="telefono"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Input
                                                                        {...field}
                                                                        className="border-gray-300"
                                                                        placeholder="Numero di telefono"
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                    {/* Campo Provincia */}
                                                    <FormField
                                                        control={form.control}
                                                        name="provincia"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Controller
                                                                        control={form.control}
                                                                        name="provincia"
                                                                        render={({ field }) => (
                                                                            <Select
                                                                                {...field}
                                                                                onValueChange={(value) => field.onChange(value)}
                                                                                value={field.value}
                                                                            >
                                                                                <SelectTrigger className="border-gray-300">
                                                                                    <SelectValue placeholder="Seleziona una provincia" />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    {provinces.map((province) => (
                                                                                        <SelectItem
                                                                                            key={province.code}
                                                                                            value={province.code}
                                                                                        >
                                                                                            {province.name}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        )}
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                {/* Campo Descrizione (textarea opzionale) */}
                                                {textarea && (
                                                    <FormField
                                                        control={form.control}
                                                        name="description"
                                                        render={({ field, fieldState }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Textarea
                                                                        {...field}
                                                                        className="border-gray-300"
                                                                        placeholder="Descrivi la tua richiesta"
                                                                    />
                                                                </FormControl>
                                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                                            </FormItem>
                                                        )}
                                                    />
                                                )}
                                                {/* Accettazione termini */}
                                                <div className="flex items-center">
                                                    <FormField
                                                        control={form.control}
                                                        name="terms"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Checkbox
                                                                        className="text-white"
                                                                        checked={field.value}
                                                                        onCheckedChange={field.onChange}
                                                                    />
                                                                </FormControl>
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <label htmlFor="terms" className="ml-2">
                                                        Accetto i{" "}
                                                        <a href="https://www.iubenda.com/termini-e-condizioni/31103949" className="text-indigo-600 underline">
                                                            termini e condizioni
                                                        </a>
                                                        .
                                                    </label>
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="w-full rounded-lg bg-gradient px-6 py-2 text-white font-bold disabled:bg-gray-400"
                                                >
                                                    {isLoading ? "Caricamento..." : submitLabel}
                                                </button>
                                            </form>
                                        </Form>
                                    </div>
                                </div>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default FormLead;
